interface SiblingSides {
  readonly leftSibling: number;
  readonly rightSibling: number;
}
const range = (start: number, end: number): number[] => {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

export enum DOTS {
  FIRST = -1,
  SECOND = -2
}

const DOTS_THRESHOLD: number = 2;
const calculateSiblings = (
  currentPage: number,
  siblingCount: number,
  totalPageCount: number
): SiblingSides => {
  const leftSibling: number = Math.max(currentPage - siblingCount, 1);
  const rightSibling: number = Math.min(currentPage + siblingCount, totalPageCount);
  return {
    leftSibling,
    rightSibling
  };
};

function generatePaginationRange(
  totalPageCount: number,
  leftDots: boolean,
  rightDots: boolean,
  leftSiblingIndex: number,
  rightSiblingIndex: number,
  siblingCount: number
): Array<number> {
  let paginationRange: Array<number>;
  const totalPageNumbers: number = siblingCount + DOTS_THRESHOLD;

  if (totalPageCount <= totalPageNumbers) {
    paginationRange = range(1, totalPageCount);
  } else if (!leftDots && rightDots) {
    const leftRange: Array<number> = range(1, Math.max(totalPageNumbers, rightSiblingIndex));
    paginationRange = [...leftRange, DOTS.SECOND, totalPageCount];
  } else if (leftDots && !rightDots) {
    const rightRange: Array<number> = range(
      Math.min(leftSiblingIndex, totalPageCount - totalPageNumbers + 1),
      totalPageCount
    );
    paginationRange = [1, DOTS.FIRST, ...rightRange];
  } else if (leftDots && rightDots) {
    const middleRange: Array<number> = range(leftSiblingIndex, rightSiblingIndex);
    paginationRange = [1, DOTS.FIRST, ...middleRange, DOTS.SECOND, totalPageCount];
  } else {
    paginationRange = range(1, totalPageCount);
  }

  return paginationRange;
}

export const generatePaginatorPages = (
  siblingCount: number,
  currentPage: number,
  totalPageCount: number
): ReadonlyArray<number> => {
  let paginationRange: Array<number> = [];
  const edgesPages: number = 2;
  const maximumPages: number = siblingCount * 2 + edgesPages + DOTS_THRESHOLD;

  if (totalPageCount <= maximumPages) {
    paginationRange = range(1, totalPageCount);
  } else {
    const { leftSibling, rightSibling }: SiblingSides = calculateSiblings(
      currentPage,
      siblingCount,
      totalPageCount
    );

    const leftDots: boolean = leftSibling - 1 > 2;
    const rightDots: boolean = rightSibling + 1 < totalPageCount - 1;

    paginationRange = generatePaginationRange(
      totalPageCount,
      leftDots,
      rightDots,
      leftSibling,
      rightSibling,
      siblingCount
    );
  }
  return paginationRange;
};
